/*
 * Created Date: Thursday May 5th 2022
 * Author: Oskar Karnblad
 * License: Proprietary to Scaffcalc AB
 *
 * Copyright (c) 2022 Scaffcalc AB
 */
import React from "react";
import { Autocomplete, TextField } from "@mui/material";
import { LocationOnOutlined, Add } from "@mui/icons-material";

export interface CustomAutocompleteProps {
  disabled?: boolean;
  error?: boolean;
  filterOptions?: any;
  freeSolo?: boolean;
  helperText?: string;
  listIcon?: any;
  id?: string;
  inputLabel?: string;
  inputValue?: any;
  noOptionsText?: string;
  onChange?: any;
  onFocus?: any;
  onInputChange?: any;
  onKeyDown?: any;
  onClose?: any;
  options: { label: string; id: string }[];
  placeholder?: string;
  required?: any;
  style?: any;
  type?: string | undefined;
  value?: any;
}

const CustomAutocomplete = React.forwardRef<
  HTMLInputElement,
  CustomAutocompleteProps
>((props, ref) => {
  const {
    disabled,
    helperText = "",
    id,
    onChange,
    onInputChange,
    onKeyDown,
    onClose,
    options,
    placeholder,
    required,
    style,
    value,
    inputValue,
    type,
    noOptionsText,
    freeSolo = true,
    error,
    filterOptions,
    listIcon,
  } = props;
  return (
    <Autocomplete
      onClose={onClose}
      freeSolo={freeSolo} // Removes the down-arrow
      id={id}
      noOptionsText={noOptionsText}
      getOptionLabel={(option) => {
        // Value selected with enter, right from the input
        if (typeof option === "string") {
          return option;
        }
        // Add "xxx" option created dynamically
        if (option.inputValue) {
          return option.inputValue;
        }
        // Regular option
        return option.label;
      }}
      filterOptions={filterOptions}
      options={options}
      sx={{
        flex: 1,
        ...style,
      }}
      inputValue={inputValue}
      onInputChange={(e, value) => onInputChange(value, e)}
      value={value}
      onChange={(e, value) => onChange(value)}
      disabled={disabled}
      ref={ref}
      ListboxProps={{
        sx: {
          backgroundColor: "background.paper",
          backgroundImage:
            "linear-gradient(rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.12))",
        },
      }}
      renderInput={(params: any) => {
        return (
          <TextField
            {...params}
            fullWidth
            error={error}
            helperText={error ? helperText : ""}
            onKeyDown={onKeyDown}
            InputProps={{
              ...params.InputProps,
              sx: {
                backgroundColor: "secondary.dark",
              },
            }}
            InputLabelProps={{
              sx: {
                userSelect: "none",
                "&.Mui-focused": {
                  color: "text.secondary",
                },
              },
            }}
            FormHelperTextProps={{
              style: {
                userSelect: "none",
              },
            }}
            sx={{
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              ...style,
            }}
            type={type}
            label={placeholder}
            margin="dense"
            required={required}
            size="small"
          />
        );
      }}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.id}>
            {option.id === "add" ? (
              <Add sx={{ color: "text.primary", marginRight: 2 }} />
            ) : listIcon ? (
              listIcon
            ) : (
              <LocationOnOutlined
                sx={{ color: "text.secondary", marginRight: 2 }}
              />
            )}
            {option.label}
          </li>
        );
      }}
    />
  );
});

export default CustomAutocomplete;
